import React, { useCallback, useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined'
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import SystemUpdateAltOutlinedIcon from '@mui/icons-material/SystemUpdateAltOutlined'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined'
import CreateUserModal from '../../components/modal/CreateUserModal'
import SceneGuestModelsLoader from './SceneGuestModelsLoader'
import SceneGuestCatalogPanel from './SceneGuestCatalogPanel'
import SceneGuestViewControls from './SceneGuestViewControls'
import SceneGuestChatPanel from './SceneGuestChatPanel'
import SceneGuestToolsDock from './SceneGuestToolsDock'
import SceneGuestActivityBridge from './SceneGuestActivityBridge'
import {
    claimSceneGuestAccess,
    getSceneGuestView,
    getSceneGuestVersion,
    openMySceneGuestAccess,
    registerSceneGuestVisitor,
    resolveSceneGuestAccess,
} from '../../actions/sceneGuestAccess.actions'
import {
    hydrateSceneSnapshot,
    resetSceneState,
    setDimensions,
    setPositions,
    setSelectedModel,
    setSelectedModelPaths,
    setSelectedModels,
} from '../../actions/sceneActions'
import { setShowModalUser } from '../../reducers/auth.reducer'
import { getSceneGuestProductModelData } from '../../actions/sceneGuestModel.actions'
import { getSocket } from '../../socket/socket'

const ModelViewer = lazy(() => import('../FenceModel/ModelViewer/ModelViewer'))

const getTokenStorageKey = (sourceKey) => `scene_guest_token:${String(sourceKey || '')}`
const getVisitorNameStorageKey = (sourceKey) => `scene_guest_name:${String(sourceKey || '')}`
const normalizePinValue = (value) => String(value || '')
    .replace(/\D/g, '')
    .slice(0, 12)

const normalizeVisitorName = (value) => String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)

const isValidVisitorName = (value) => normalizeVisitorName(value).length >= 2

const getAuthenticatedVisitorName = (auth = {}) => {
    if (!auth.authenticate) return ''

    const user = auth.user || {}
    const direct = normalizeVisitorName(
        user.fullName ||
        user.displayName ||
        user.name
    )

    if (direct) return direct

    const parts = [user.firstName, user.lastName]
        .map(normalizeVisitorName)
        .filter(Boolean)
    const joined = normalizeVisitorName(parts.join(' '))

    if (joined) return joined

    const username = normalizeVisitorName(user.username)
    if (username) return username

    return normalizeVisitorName(String(user.email || '').split('@')[0])
}

const getBrowserStorage = (name) => {
    if (typeof window === 'undefined') return null

    try {
        return window[name] || null
    } catch (_error) {
        return null
    }
}

const safeStorageGet = (storage, key) => {
    if (!storage || !key) return ''

    try {
        return storage.getItem(key) || ''
    } catch (_error) {
        return ''
    }
}

const safeStorageSet = (storage, key, value) => {
    if (!storage || !key) return false

    try {
        storage.setItem(key, String(value ?? ''))
        return true
    } catch (_error) {
        return false
    }
}

const safeStorageRemove = (storage, key) => {
    if (!storage || !key) return false

    try {
        storage.removeItem(key)
        return true
    } catch (_error) {
        return false
    }
}

const getOrCreateStorageId = (storage, key, prefix) => {
    const fallback = `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
    if (!storage) return fallback

    let value = safeStorageGet(storage, key)

    if (!value) {
        value = fallback
        safeStorageSet(storage, key, value)
    }

    return value
}

const isPinChallengeResult = (result = {}) => {
    const code = String(result?.code || '').trim().toUpperCase()
    const message = String(result?.error || '').toLowerCase()

    if (result?.requiresPin === true) return true

    if ([
        'SCENE_GUEST_PIN_REQUIRED',
        'SCENE_GUEST_PIN_INVALID',
        'PIN_REQUIRED',
        'PIN_INVALID',
    ].includes(code)) {
        return true
    }

    return (
        [400, 401, 403].includes(Number(result?.status || 0)) &&
        /\bpin\b|пин/.test(message)
    )
}

const GUEST_TOKEN_REJECTION_CODES = new Set([
    'SCENE_GUEST_TOKEN_INVALID',
    'SCENE_GUEST_TOKEN_REPLACED',
    'SCENE_GUEST_ACCESS_NOT_FOUND',
    'SCENE_GUEST_ACCESS_REVOKED',
    'SCENE_GUEST_ACCESS_EXPIRED',
    'SCENE_GUEST_ACCESS_NOT_STARTED',
])

const isGuestTokenRejectedResult = (result = {}) => {
    const code = String(result?.code || '').trim().toUpperCase()
    const status = Number(result?.status || 0)

    if (GUEST_TOKEN_REJECTION_CODES.has(code)) return true

    return status === 401
}

const formatDate = (value) => {
    if (!value) return 'без ограничения'

    const date = new Date(value)

    if (Number.isNaN(date.getTime())) return 'без ограничения'

    return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const hasGuestEditPermissions = (permissions = {}) => !!(
    permissions.moveObjects ||
    permissions.rotateObjects ||
    permissions.scaleObjects ||
    permissions.changeQuantity ||
    permissions.useArrayModifier ||
    permissions.useMirrorModifier ||
    permissions.monumentAssembly
)

const getAccessRuntimeSignature = (access = {}) => JSON.stringify({
    status: access.status || '',
    expiresAt: access.expiresAt || null,
    permissions: access.permissions || {},
    limits: access.limits || {},
    accessMode: access.accessMode || '',
    catalogMode: access.catalogMode || '',
})

const SceneGuestViewerPage = ({
    setShowFooter,
    savedProject = false,
}) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const params = useParams()
    const auth = useSelector((state) => state.auth || {})
    const accessKey = String(params.accessKey || '').trim()
    const grantId = String(params.grantId || '').trim()
    const sourceKey = savedProject ? `project:${grantId}` : `access:${accessKey}`
    const storageKey = getTokenStorageKey(sourceKey)
    const authenticatedVisitorName = useMemo(
        () => getAuthenticatedVisitorName(auth),
        [auth]
    )

    const [guestToken, setGuestToken] = useState(() => (
        safeStorageGet(getBrowserStorage('sessionStorage'), storageKey)
    ))
    const [data, setData] = useState(null)
    const [loadedSourceKey, setLoadedSourceKey] = useState('')
    const [pin, setPin] = useState('')
    const [loading, setLoading] = useState(true)
    const [resolving, setResolving] = useState(false)
    const [requiresPin, setRequiresPin] = useState(false)
    const [error, setError] = useState('')
    const [claimed, setClaimed] = useState(false)
    const [claiming, setClaiming] = useState(false)
    const [pendingClaim, setPendingClaim] = useState(false)
    const [modelProgress, setModelProgress] = useState({
        total: 0,
        loaded: 0,
        failed: 0,
        loading: false,
        phase: 'done',
    })
    const [catalogOpen, setCatalogOpen] = useState(false)
    const [viewControlsOpen, setViewControlsOpen] = useState(false)
    const [previewingProductId, setPreviewingProductId] = useState('')
    const [updateAvailable, setUpdateAvailable] = useState(null)
    const [refreshingScene, setRefreshingScene] = useState(false)
    const [visitor, setVisitor] = useState(null)
    const [visitorNameDraft, setVisitorNameDraft] = useState(() => (
        safeStorageGet(
            getBrowserStorage('localStorage'),
            getVisitorNameStorageKey(sourceKey)
        )
    ))
    const [visitorNameError, setVisitorNameError] = useState('')
    const [visitorRegistering, setVisitorRegistering] = useState(false)
    const [guestChat, setGuestChat] = useState(null)
    const [workspaceOpen, setWorkspaceOpen] = useState(false)
    const [workspaceTab, setWorkspaceTab] = useState('chat')
    const [toolsOpen, setToolsOpen] = useState(true)
    const [toolsTab, setToolsTab] = useState('transform')
    const [mobileHeaderOpen, setMobileHeaderOpen] = useState(false)
    const [guestSceneDirty, setGuestSceneDirty] = useState(false)
    const markGuestSceneDirty = useCallback(() => {
        setGuestSceneDirty(true)
    }, [])
    const hydratedSceneIdRef = useRef('')
    const registeredTokenRef = useRef('')
    const deviceIdRef = useRef('')
    const pageSessionIdRef = useRef('')
    const accessSignatureRef = useRef('')
    const accessRequestIdRef = useRef(0)
    const resolveRequestIdRef = useRef(0)
    const openingRef = useRef(false)
    const guestTokenRef = useRef(guestToken)
    const dataRef = useRef(data)
    const profileAutoRegisterKeyRef = useRef('')
    const activeSourceKeyRef = useRef(sourceKey)

    activeSourceKeyRef.current = sourceKey

    if (typeof window !== 'undefined' && !deviceIdRef.current) {
        deviceIdRef.current = getOrCreateStorageId(
            getBrowserStorage('localStorage'),
            'scene_guest_device_id',
            'device'
        )
        pageSessionIdRef.current = getOrCreateStorageId(
            getBrowserStorage('sessionStorage'),
            `scene_guest_page_session:${sourceKey}`,
            'visit'
        )
    }

    useEffect(() => {
        setShowFooter?.(false)

        return () => {
            setShowFooter?.(true)
        }
    }, [setShowFooter])

    useEffect(() => {
        guestTokenRef.current = guestToken
    }, [guestToken])

    useEffect(() => {
        dataRef.current = data
    }, [data])

    useEffect(() => {
        const sessionStorage = getBrowserStorage('sessionStorage')
        const localStorage = getBrowserStorage('localStorage')
        const nextGuestToken = safeStorageGet(sessionStorage, storageKey)
        const nextVisitorName = safeStorageGet(
            localStorage,
            getVisitorNameStorageKey(sourceKey)
        )

        activeSourceKeyRef.current = sourceKey
        accessRequestIdRef.current += 1
        resolveRequestIdRef.current += 1
        openingRef.current = false
        guestTokenRef.current = nextGuestToken
        dataRef.current = null
        hydratedSceneIdRef.current = ''
        registeredTokenRef.current = ''
        accessSignatureRef.current = ''
        profileAutoRegisterKeyRef.current = ''
        pageSessionIdRef.current = getOrCreateStorageId(
            sessionStorage,
            `scene_guest_page_session:${sourceKey}`,
            'visit'
        )

        dispatch(resetSceneState({
            reason: 'scene-guest-source-changed',
            sceneContextKey: `guest:${sourceKey}`,
        }))

        setGuestToken(nextGuestToken)
        setData(null)
        setLoadedSourceKey('')
        setPin('')
        setLoading(true)
        setResolving(false)
        setRequiresPin(false)
        setError('')
        setClaimed(false)
        setClaiming(false)
        setPendingClaim(false)
        setModelProgress({
            total: 0,
            loaded: 0,
            failed: 0,
            loading: false,
            phase: 'done',
        })
        setCatalogOpen(false)
        setViewControlsOpen(false)
        setPreviewingProductId('')
        setUpdateAvailable(null)
        setRefreshingScene(false)
        setVisitor(null)
        setVisitorNameDraft(normalizeVisitorName(nextVisitorName))
        setVisitorNameError('')
        setVisitorRegistering(false)
        setGuestChat(null)
        setWorkspaceOpen(false)
        setWorkspaceTab('chat')
        setToolsOpen(true)
        setToolsTab('transform')
        setMobileHeaderOpen(false)
        setGuestSceneDirty(false)
    }, [dispatch, sourceKey, storageKey])

    const persistToken = useCallback((token) => {
        const normalizedToken = String(token || '')
        setGuestToken(normalizedToken)

        const storage = getBrowserStorage('sessionStorage')

        if (normalizedToken) {
            safeStorageSet(storage, storageKey, normalizedToken)
        } else {
            safeStorageRemove(storage, storageKey)
        }
    }, [storageKey])

    const applyPayload = useCallback((payload, expectedSourceKey = sourceKey) => {
        if (String(expectedSourceKey || '') !== activeSourceKeyRef.current) return false

        const snapshot = payload?.scene?.snapshot || {}
        const sceneId = String(
            payload?.scene?._id ||
            payload?.access?._id ||
            sourceKey
        )
        const payloadGrantId = String(payload?.access?._id || '')

        if (savedProject && grantId && payloadGrantId && payloadGrantId !== grantId) {
            return false
        }

        if (!snapshot || !Object.keys(snapshot).length) return false

        const sceneContextKey = `guest:${sceneId}`

        hydratedSceneIdRef.current = sceneId
        accessSignatureRef.current = getAccessRuntimeSignature(payload?.access || {})

        // Гостевая сцена является авторитетным снимком. Сначала полностью
        // очищаем runtime предыдущей сцены, затем гидратируем только данные
        // текущего доступа. Это не даёт productsMeta/settings/объектам старой
        // сцены просочиться через merge-логику общего scene reducer.
        dispatch(resetSceneState({
            reason: 'scene-guest-authoritative-hydrate',
            sceneContextKey,
        }))
        dispatch(hydrateSceneSnapshot(snapshot, {
            source: 'scene-guest-access',
            officeId: payload?.office?._id || payload?.access?.officeId || null,
            salesSessionId: payload?.session?.sessionId || payload?.access?.salesSessionId || '',
            sceneContextKey,
        }))

        // ModelViewer появляется только после синхронной Redux-гидратации
        // правильного snapshot, поэтому предыдущая сцена не успевает мигнуть.
        dataRef.current = payload
        setLoadedSourceKey(expectedSourceKey)
        setData(payload)
        setGuestChat(payload?.chat || null)
        setClaimed(!!payload?.access?.claimedByUserId)
        setError('')
        setRequiresPin(false)
        setUpdateAvailable(null)
        setGuestSceneDirty(false)
        return true
    }, [dispatch, grantId, savedProject, sourceKey])

    const applyAccessUpdate = useCallback((nextAccess) => {
        if (!nextAccess) return false

        const nextSignature = getAccessRuntimeSignature(nextAccess)

        if (nextSignature === accessSignatureRef.current) return false

        accessSignatureRef.current = nextSignature
        setData((current) => ({
            ...(current || {}),
            access: {
                ...(current?.access || {}),
                ...nextAccess,
            },
        }))

        if (hasGuestEditPermissions(nextAccess.permissions)) {
            setToolsOpen(true)
        }

        return true
    }, [])

    const loadByToken = useCallback(async (token) => {
        const normalizedToken = String(token || '').trim()

        if (!normalizedToken) {
            return {
                ok: false,
                tokenRejected: true,
            }
        }

        const requestId = ++accessRequestIdRef.current
        const requestSourceKey = sourceKey

        setLoading(true)
        setError('')

        const result = await dispatch(getSceneGuestView(normalizedToken))

        if (
            requestId !== accessRequestIdRef.current ||
            requestSourceKey !== activeSourceKeyRef.current
        ) {
            return {
                ok: false,
                cancelled: true,
            }
        }

        setLoading(false)

        if (!result?.ok) {
            const tokenRejected = isGuestTokenRejectedResult(result)

            if (tokenRejected) {
                persistToken('')
            }

            setError(
                tokenRejected
                    ? (result?.error || 'Гостевой доступ недействителен')
                    : (result?.error || 'Не удалось загрузить сцену. Повторите попытку.')
            )

            return {
                ok: false,
                tokenRejected,
            }
        }

        if (!applyPayload(result.data, requestSourceKey)) {
            return {
                ok: false,
                cancelled: true,
            }
        }

        return {
            ok: true,
            tokenRejected: false,
        }
    }, [applyPayload, dispatch, persistToken, sourceKey])

    const openSavedProject = useCallback(async () => {
        if (!savedProject || !grantId) return false

        const requestId = ++accessRequestIdRef.current
        const requestSourceKey = sourceKey

        setLoading(true)
        setError('')

        const result = await dispatch(openMySceneGuestAccess(grantId))

        if (
            requestId !== accessRequestIdRef.current ||
            requestSourceKey !== activeSourceKeyRef.current
        ) return false

        setLoading(false)

        if (!result?.ok) {
            setError(result?.error || 'Не удалось открыть сохранённый проект')
            return false
        }

        if (!applyPayload(result.data, requestSourceKey)) return false

        persistToken(result.data.guestToken)
        return true
    }, [
        applyPayload,
        dispatch,
        grantId,
        persistToken,
        savedProject,
        sourceKey,
    ])

    const resolveAccess = useCallback(async (pinValue = '', options = {}) => {
        if (!accessKey || savedProject) return false

        const normalizedPin = normalizePinValue(pinValue)
        const isPinAttempt = normalizedPin.length > 0
        const requestId = ++resolveRequestIdRef.current
        const requestSourceKey = sourceKey
        const timeoutMs = Math.max(
            5000,
            Number(options.timeoutMs) || (isPinAttempt ? 20000 : 10000)
        )

        if (isPinAttempt) {
            setResolving(true)
            setRequiresPin(true)
        } else {
            setLoading(true)
        }

        setError('')

        try {
            const result = await dispatch(resolveSceneGuestAccess({
                accessKey,
                pin: normalizedPin,
                timeoutMs,
            }))

            if (
                requestId !== resolveRequestIdRef.current ||
                requestSourceKey !== activeSourceKeyRef.current
            ) return false

            if (!result?.ok) {
                const pinNeeded = isPinChallengeResult(result)
                const automaticCheckFailed = !isPinAttempt && (
                    result?.timedOut ||
                    result?.networkError ||
                    Number(result?.status || 0) === 0
                )

                if (pinNeeded || automaticCheckFailed) {
                    setRequiresPin(true)

                    if (isPinAttempt) {
                        setError(result?.error || 'PIN-код не подошёл')
                    } else if (pinNeeded) {
                        setError('')
                    } else {
                        setError(
                            'Автоматическая проверка не завершилась. Введите PIN-код и нажмите «Открыть сцену».'
                        )
                    }

                    return false
                }

                setRequiresPin(false)
                setError(result?.error || 'Не удалось открыть сцену')
                return false
            }

            const resolvedToken = String(result.data?.guestToken || '').trim()

            if (!resolvedToken) {
                setRequiresPin(isPinAttempt)
                setError('Сервер подтвердил доступ, но не выдал гостевой токен')
                return false
            }

            persistToken(resolvedToken)
            setRequiresPin(false)
            setLoading(true)

            const viewResult = await dispatch(getSceneGuestView(resolvedToken))

            if (
                requestId !== resolveRequestIdRef.current ||
                requestSourceKey !== activeSourceKeyRef.current
            ) return false

            if (!viewResult?.ok) {
                const tokenRejected = isGuestTokenRejectedResult(viewResult)

                if (tokenRejected) {
                    persistToken('')
                    setRequiresPin(isPinAttempt)
                }

                setError(
                    tokenRejected
                        ? (viewResult?.error || 'Гостевой доступ недействителен')
                        : `Доступ подтверждён, но сцена пока не загрузилась: ${
                            viewResult?.error || 'повторите попытку'
                        }`
                )

                return false
            }

            if (!applyPayload(viewResult.data, requestSourceKey)) return false
            return true
        } catch (requestError) {
            if (
                requestId !== resolveRequestIdRef.current ||
                requestSourceKey !== activeSourceKeyRef.current
            ) return false

            setError(
                requestError?.message ||
                'Не удалось завершить открытие гостевой сцены'
            )
            return false
        } finally {
            if (
                requestId === resolveRequestIdRef.current &&
                requestSourceKey === activeSourceKeyRef.current
            ) {
                setResolving(false)
                setLoading(false)
            }
        }
    }, [
        accessKey,
        applyPayload,
        dispatch,
        persistToken,
        savedProject,
        sourceKey,
    ])

    useEffect(() => {
        let cancelled = false

        const open = async () => {
            if (dataRef.current || openingRef.current) return

            openingRef.current = true

            try {
                if (savedProject) {
                    if (!auth.authenticate) {
                        setLoading(false)
                        setError('Войдите в аккаунт, чтобы открыть сохранённый проект')
                        return
                    }

                    await openSavedProject()
                    return
                }

                if (!accessKey) {
                    setLoading(false)
                    setError('Код доступа не указан')
                    return
                }

                const storedToken = guestTokenRef.current ||
                    safeStorageGet(getBrowserStorage('sessionStorage'), storageKey)

                if (storedToken) {
                    const tokenResult = await loadByToken(storedToken)

                    if (tokenResult?.ok || cancelled) return

                    if (!tokenResult?.tokenRejected) return
                }

                if (!cancelled) {
                    await resolveAccess('', { timeoutMs: 10000 })
                }
            } finally {
                openingRef.current = false
            }
        }

        open()

        return () => {
            cancelled = true
            accessRequestIdRef.current += 1
            resolveRequestIdRef.current += 1
            openingRef.current = false
        }
    }, [
        accessKey,
        auth.authenticate,
        loadByToken,
        openSavedProject,
        resolveAccess,
        savedProject,
        sourceKey,
        storageKey,
    ])

    useEffect(() => {
        if (!data?.access?._id || visitor) return

        const savedName = safeStorageGet(
            getBrowserStorage('localStorage'),
            getVisitorNameStorageKey(sourceKey)
        )

        if (savedName) {
            setVisitorNameDraft(normalizeVisitorName(savedName))
        }
    }, [data?.access?._id, sourceKey, visitor])

    const registerVisitorByName = useCallback(async (rawName) => {
        if (!guestToken || !data?.access?._id || visitorRegistering) return false

        const cleanName = normalizeVisitorName(rawName)

        if (!isValidVisitorName(cleanName)) {
            setVisitorNameError('Введите имя: минимум 2 символа')
            return false
        }

        const requestSourceKey = sourceKey

        setVisitorRegistering(true)
        setVisitorNameError('')
        setError('')

        const result = await dispatch(registerSceneGuestVisitor(guestToken, {
            name: cleanName,
            deviceId: deviceIdRef.current,
            pageSessionId: pageSessionIdRef.current,
        }))

        if (requestSourceKey !== activeSourceKeyRef.current) return false

        setVisitorRegistering(false)

        if (!result?.ok) {
            const message = result?.error || 'Не удалось зарегистрировать посетителя'
            setVisitorNameError(message)
            return false
        }

        const nextToken = result.data?.guestToken || guestToken
        const nextVisitor = result.data?.visitor || null
        const nextAccess = result.data?.access || null

        if (!nextVisitor) {
            setVisitorNameError('Сервер не вернул данные посетителя')
            return false
        }

        registeredTokenRef.current = nextToken
        setVisitor(nextVisitor)
        setVisitorNameDraft(nextVisitor.name || cleanName)
        setGuestChat(result.data?.chat || data?.chat || null)

        if (nextAccess) applyAccessUpdate(nextAccess)

        safeStorageSet(
            getBrowserStorage('localStorage'),
            getVisitorNameStorageKey(sourceKey),
            nextVisitor.name || cleanName
        )

        if (nextToken !== guestToken) persistToken(nextToken)
        return true
    }, [
        applyAccessUpdate,
        data?.access?._id,
        data?.chat,
        dispatch,
        guestToken,
        persistToken,
        sourceKey,
        visitorRegistering,
    ])

    const registerVisitor = useCallback((event) => {
        event?.preventDefault?.()
        return registerVisitorByName(visitorNameDraft)
    }, [registerVisitorByName, visitorNameDraft])

    useEffect(() => {
        if (
            !authenticatedVisitorName ||
            !guestToken ||
            !data?.access?._id ||
            visitor
        ) {
            return
        }

        const key = [
            guestToken,
            data.access._id,
            authenticatedVisitorName,
        ].join(':')

        if (profileAutoRegisterKeyRef.current === key) return

        profileAutoRegisterKeyRef.current = key
        setVisitorNameDraft(authenticatedVisitorName)
        registerVisitorByName(authenticatedVisitorName)
    }, [
        authenticatedVisitorName,
        data?.access?._id,
        guestToken,
        registerVisitorByName,
        visitor,
    ])

    const handleVisitorChange = useCallback((nextVisitor) => {
        if (!nextVisitor) return
        setVisitor(nextVisitor)

        if (nextVisitor.name) {
            safeStorageSet(
                getBrowserStorage('localStorage'),
                getVisitorNameStorageKey(sourceKey),
                nextVisitor.name
            )
        }
    }, [sourceKey])

    useEffect(() => {
        if (
            loadedSourceKey !== sourceKey ||
            !guestToken ||
            !visitor?._id ||
            !guestChat?._id
        ) return undefined

        const socket = getSocket()
        const currentGrantId = String(data?.access?._id || '')
        const currentPageSessionId = pageSessionIdRef.current
        const join = () => {
            socket.emit('sceneGuest:join', {
                guestToken,
                pageSessionId: currentPageSessionId,
            })
        }
        const handleSceneUpdated = (payload = {}) => {
            const payloadGrantId = String(payload.grantId || '')

            if (payloadGrantId && currentGrantId && payloadGrantId !== currentGrantId) return

            setUpdateAvailable({
                ...(payload.scene || {}),
                message: payload.message || 'Менеджер обновил сцену',
                description: payload.description || 'Доступна новая версия проекта',
            })
        }
        const handleAccessUpdated = (payload = {}) => {
            const payloadGrantId = String(payload.grantId || '')

            if (payloadGrantId && currentGrantId && payloadGrantId !== currentGrantId) return
            if (!payload.access) return

            applyAccessUpdate(payload.access)
        }

        socket.on('connect', join)
        socket.on('sceneGuest:sceneUpdated', handleSceneUpdated)
        socket.on('sceneGuest:accessUpdated', handleAccessUpdated)
        join()

        return () => {
            socket.off('connect', join)
            socket.off('sceneGuest:sceneUpdated', handleSceneUpdated)
            socket.off('sceneGuest:accessUpdated', handleAccessUpdated)
            socket.emit('sceneGuest:leave', {
                chatId: guestChat._id,
                pageSessionId: currentPageSessionId,
            })
        }
    }, [
        applyAccessUpdate,
        data?.access?._id,
        guestChat?._id,
        guestToken,
        loadedSourceKey,
        sourceKey,
        visitor?._id,
    ])

    useEffect(() => {
        return () => {
            dispatch(resetSceneState({
                reason: 'scene-guest-view-closed',
                sceneContextKey: `guest:${hydratedSceneIdRef.current || sourceKey}`,
            }))

            safeStorageRemove(
                getBrowserStorage('sessionStorage'),
                `scene_guest_page_session:${sourceKey}`
            )
        }
    }, [dispatch, sourceKey])

    const handlePinSubmit = async (event) => {
        event.preventDefault()

        const normalizedPin = normalizePinValue(pin)

        if (!normalizedPin) return

        setPin(normalizedPin)
        await resolveAccess(normalizedPin)
    }

    const saveToAccount = useCallback(async () => {
        if (!guestToken || claiming || claimed) return

        if (!auth.authenticate) {
            setPendingClaim(true)
            dispatch(setShowModalUser({
                forceShow: true,
                mode: 'signup',
                role: 'user',
            }))
            return
        }

        const requestSourceKey = sourceKey

        setClaiming(true)
        setError('')

        const result = await dispatch(claimSceneGuestAccess(guestToken))

        if (requestSourceKey !== activeSourceKeyRef.current) return

        setClaiming(false)

        if (!result?.ok) {
            setError(result?.error || 'Не удалось сохранить проект')
            return
        }

        setClaimed(true)
        setPendingClaim(false)
        setData((current) => ({
            ...(current || {}),
            access: {
                ...(current?.access || {}),
                ...(result.data?.access || {}),
            },
        }))
    }, [
        auth.authenticate,
        claimed,
        claiming,
        dispatch,
        guestToken,
        sourceKey,
    ])

    useEffect(() => {
        if (!pendingClaim || !auth.authenticate || claimed || claiming) return

        saveToAccount()
    }, [
        auth.authenticate,
        claimed,
        claiming,
        pendingClaim,
        saveToAccount,
    ])

    const handleModelProgress = useCallback((progress) => {
        setModelProgress(progress)
    }, [])

    const refreshGuestScene = useCallback(async () => {
        if (!guestToken || refreshingScene) return false

        if (guestSceneDirty && typeof window !== 'undefined') {
            const confirmed = window.confirm(
                'У вас есть локальные изменения. При обновлении загрузится версия менеджера, а ваши текущие правки будут заменены. Продолжить?'
            )

            if (!confirmed) return false
        }

        const requestSourceKey = sourceKey

        setRefreshingScene(true)
        setError('')

        const result = await dispatch(getSceneGuestView(guestToken))

        if (requestSourceKey !== activeSourceKeyRef.current) return false

        setRefreshingScene(false)

        if (!result?.ok) {
            setError(result?.error || 'Не удалось обновить сцену')
            return false
        }

        return applyPayload(result.data, requestSourceKey)
    }, [applyPayload, dispatch, guestSceneDirty, guestToken, refreshingScene, sourceKey])

    const currentSceneRevision = Number(data?.scene?.revision || 1)
    const currentSceneUpdatedAt = String(data?.scene?.updatedAt || '')

    useEffect(() => {
        if (!guestToken || !data?.scene) return undefined

        let cancelled = false
        let inFlight = false
        let timer = null

        let check = null

        const scheduleNext = (delay = 30000) => {
            if (cancelled || typeof window === 'undefined' || !check) return
            if (timer !== null) window.clearTimeout(timer)
            timer = window.setTimeout(() => check(), delay)
        }

        check = async (force = false) => {
            if (cancelled || inFlight) return

            if (!force && typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                scheduleNext(30000)
                return
            }

            inFlight = true
            const result = await dispatch(getSceneGuestVersion(guestToken))
            inFlight = false

            if (cancelled) return

            if (result?.ok) {
                const nextAccess = result.data?.access || null

                if (nextAccess) applyAccessUpdate(nextAccess)

                const nextRevision = Number(result.data?.scene?.revision || 1)
                const nextUpdatedAt = String(result.data?.scene?.updatedAt || '')

                if (
                    nextRevision > currentSceneRevision ||
                    (nextUpdatedAt && currentSceneUpdatedAt && nextUpdatedAt !== currentSceneUpdatedAt)
                ) {
                    setUpdateAvailable(result.data.scene)
                }
            }

            scheduleNext(30000)
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') check(true)
        }

        check(true)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            cancelled = true
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            if (timer !== null) window.clearTimeout(timer)
        }
    }, [
        applyAccessUpdate,
        currentSceneRevision,
        currentSceneUpdatedAt,
        data?.scene,
        dispatch,
        guestToken,
    ])

    const handlePreviewProduct = useCallback(async (product) => {
        const productId = String(product?._id || product?.id || '').trim()

        if (!guestToken || !productId || previewingProductId) return

        const requestSourceKey = sourceKey

        setPreviewingProductId(productId)
        setError('')

        try {
            const action = await dispatch(getSceneGuestProductModelData(guestToken, productId))

            if (requestSourceKey !== activeSourceKeyRef.current) return

            const dimensions = action?.payload?.dimensions || null

            dispatch(setSelectedModels({ guestPreview: productId }))
            dispatch(setPositions({ [productId]: [0, 0, 0] }))
            dispatch(setSelectedModel(productId))
            dispatch(setSelectedModelPaths([productId]))

            if (dimensions) {
                dispatch(setDimensions({ [productId]: dimensions }))
            }

            setData((current) => {
                const currentProducts = Array.isArray(current?.products) ? current.products : []
                const exists = currentProducts.some((item) => String(item?._id || item?.id || '') === productId)

                return {
                    ...(current || {}),
                    products: exists ? currentProducts : [...currentProducts, product],
                }
            })

            setCatalogOpen(false)
        } catch (previewError) {
            if (requestSourceKey === activeSourceKeyRef.current) {
                setError(
                    previewError?.response?.data?.message ||
                    previewError?.message ||
                    'Не удалось загрузить 3D-модель товара'
                )
            }
        } finally {
            if (requestSourceKey === activeSourceKeyRef.current) {
                setPreviewingProductId('')
            }
        }
    }, [dispatch, guestToken, previewingProductId, sourceKey])

    const sourceDataReady = loadedSourceKey === sourceKey && !!data
    const permissions = sourceDataReady ? (data?.access?.permissions || {}) : {}
    const limits = sourceDataReady ? (data?.access?.limits || {}) : {}
    const editEnabled = hasGuestEditPermissions(permissions)
    const catalogEnabled = !!data?.catalog?.enabled
    const expiresText = useMemo(
        () => formatDate(data?.access?.expiresAt),
        [data?.access?.expiresAt]
    )

    if (!sourceDataReady) {
        return (
            <main className="sceneGuestUnlock">
                <section className="sceneGuestUnlock__card">
                    <div className="sceneGuestUnlock__icon">
                        <LockOutlinedIcon />
                    </div>

                    <div>
                        <span>Защищённая 3D-сцена</span>
                        <h1>
                            {requiresPin
                                ? 'Введите PIN-код'
                                : (loading || loadedSourceKey !== sourceKey)
                                    ? 'Проверяем доступ'
                                    : 'Сцена не открыта'}
                        </h1>
                        <p>
                            {requiresPin
                                ? 'Менеджер включил дополнительную защиту для этой ссылки.'
                                : 'Доступ проверяется только для конкретной сцены и не открывает данные офиса.'}
                        </p>
                    </div>

                    {!savedProject ? (
                        <form onSubmit={handlePinSubmit}>
                            <label>
                                <span>PIN</span>
                                <input
                                    type="password"
                                    name="sceneGuestPin"
                                    value={pin}
                                    onChange={(event) => setPin(normalizePinValue(event.currentTarget.value))}
                                    placeholder="Введите PIN"
                                    autoComplete="one-time-code"
                                    inputMode="numeric"
                                    pattern="\d{4,12}"
                                    minLength={4}
                                    maxLength={12}
                                    enterKeyHint="go"
                                    autoFocus={requiresPin || !loading}
                                />
                            </label>

                            <button type="submit" disabled={resolving || normalizePinValue(pin).length < 4}>
                                {resolving ? 'Проверяем…' : 'Открыть сцену'}
                            </button>

                            {!requiresPin && loading ? (
                                <small>
                                    Автоматически проверяем ссылку. PIN можно ввести сразу, не дожидаясь завершения проверки.
                                </small>
                            ) : null}
                        </form>
                    ) : null}

                    {error ? (
                        <div className="sceneGuestUnlock__error">
                            {error}
                        </div>
                    ) : null}

                    <div className="sceneGuestUnlock__actions">
                        {!loading ? (
                            <button
                                type="button"
                                onClick={() => {
                                    if (savedProject) {
                                        openSavedProject()
                                    } else if (guestToken) {
                                        loadByToken(guestToken)
                                    } else {
                                        resolveAccess('', { timeoutMs: 10000 })
                                    }
                                }}
                            >
                                <RefreshOutlinedIcon />
                                Повторить проверку
                            </button>
                        ) : null}

                        <button
                            type="button"
                            className="is-secondary"
                            onClick={() => navigate(savedProject ? '/mySceneProjects' : '/scene-access')}
                        >
                            <ArrowBackOutlinedIcon />
                            Назад
                        </button>
                    </div>
                </section>

                <CreateUserModal compact />
            </main>
        )
    }

    if (guestToken && data?.access?._id && !visitor) {
        if (authenticatedVisitorName) {
            return (
                <main className="sceneGuestUnlock sceneGuestNameGate">
                    <section className="sceneGuestUnlock__card sceneGuestNameGate__card">
                        <div className="sceneGuestNameGate__icon">
                            <CheckCircleOutlineIcon />
                        </div>

                        <div className="sceneGuestNameGate__copy">
                            <span>Гостевой 3D-доступ</span>
                            <h1>Здравствуйте, {authenticatedVisitorName}</h1>
                            <p>
                                Профиль уже определён. Подключаем вас к сцене без повторного ввода имени.
                            </p>
                        </div>

                        <form
                            className="sceneGuestNameGate__form"
                            onSubmit={(event) => {
                                event.preventDefault()
                                registerVisitorByName(authenticatedVisitorName)
                            }}
                        >
                            {visitorNameError ? (
                                <div className="sceneGuestNameGate__error">
                                    {visitorNameError}
                                </div>
                            ) : null}

                            <button
                                type="submit"
                                disabled={visitorRegistering}
                            >
                                {visitorRegistering ? 'Подключаем…' : 'Войти в сцену'}
                                <CheckCircleOutlineIcon />
                            </button>
                        </form>
                    </section>
                </main>
            )
        }

        return (
            <main className="sceneGuestUnlock sceneGuestNameGate">
                <section className="sceneGuestUnlock__card sceneGuestNameGate__card">
                    <div className="sceneGuestNameGate__icon">
                        <PersonAddAltOutlinedIcon />
                    </div>

                    <div className="sceneGuestNameGate__copy">
                        <span>Гостевой 3D-доступ</span>
                        <h1>Как к вам обращаться?</h1>
                        <p>
                            Имя увидит менеджер и другие участники этой сцены. Каждый посетитель
                            указывает своё имя отдельно.
                        </p>
                    </div>

                    <form className="sceneGuestNameGate__form" onSubmit={registerVisitor}>
                        <label>
                            <span>Ваше имя</span>
                            <input
                                value={visitorNameDraft}
                                onChange={(event) => {
                                    setVisitorNameDraft(event.target.value)
                                    if (visitorNameError) setVisitorNameError('')
                                }}
                                placeholder="Например, Виктория"
                                autoComplete="name"
                                maxLength={80}
                                autoFocus
                            />
                        </label>

                        {visitorNameError ? (
                            <div className="sceneGuestNameGate__error">
                                {visitorNameError}
                            </div>
                        ) : null}

                        <button
                            type="submit"
                            disabled={visitorRegistering || !isValidVisitorName(visitorNameDraft)}
                        >
                            {visitorRegistering ? 'Подключаем…' : 'Войти в сцену'}
                            <CheckCircleOutlineIcon />
                        </button>
                    </form>
                </section>
            </main>
        )
    }

    return (
        <main className="sceneGuestView">
            <SceneGuestModelsLoader
                guestToken={guestToken}
                initialProductIds={data.manifest?.initialProductIds || []}
                deferredProductIds={data.manifest?.deferredProductIds || []}
                onProgress={handleModelProgress}
            />

            <SceneGuestActivityBridge
                enabled={!!(guestToken && visitor?._id && data?.access?._id)}
                guestToken={guestToken}
                visitorId={visitor?._id || ''}
                grantId={data?.access?._id || ''}
                pageSessionId={pageSessionIdRef.current}
                resetKey={`${data?.scene?._id || ''}:${data?.scene?.revision || 0}:${guestToken}:${visitor?._id || ''}`}
                onDirty={markGuestSceneDirty}
            />

            <div className="sceneGuestView__topbar">
                <button
                    type="button"
                    className="sceneGuestView__mobileHeaderToggle"
                    onClick={() => setMobileHeaderOpen((current) => !current)}
                    aria-expanded={mobileHeaderOpen}
                >
                    <span>Проект и действия</span>
                    {Number(visitor?.unreadCount || 0) > 0 ? (
                        <span className="sceneGuestView__unread">{visitor.unreadCount}</span>
                    ) : null}
                    {mobileHeaderOpen ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon />}
                </button>

                <header className={`sceneGuestView__header ${mobileHeaderOpen ? 'is-mobile-open' : 'is-mobile-collapsed'}`}>
                <div className="sceneGuestView__title">
                    <span>Гостевой 3D-проект</span>
                    <strong>{data.scene?.title || data.session?.title || '3D-сцена'}</strong>
                    <small>
                        {visitor?.name ? `Здравствуйте, ${visitor.name} · ` : ''}
                        {data.office?.name || 'Офис'} · доступ {expiresText}
                    </small>
                </div>

                <div className="sceneGuestView__headerActions">
                    <button
                        type="button"
                        className="is-view"
                        onClick={() => setViewControlsOpen(true)}
                    >
                        <TuneOutlinedIcon />
                        Вид
                    </button>

                    {catalogEnabled ? (
                        <button
                            type="button"
                            className="is-catalog"
                            onClick={() => setCatalogOpen(true)}
                        >
                            <Inventory2OutlinedIcon />
                            Каталог офиса
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className={workspaceOpen && workspaceTab === 'chat' ? 'is-active' : ''}
                        onClick={() => {
                            setWorkspaceTab('chat')
                            setWorkspaceOpen(true)
                        }}
                    >
                        <ChatOutlinedIcon />
                        Чат
                        {Number(visitor?.unreadCount || 0) > 0 ? (
                            <span className="sceneGuestView__unread">{visitor.unreadCount}</span>
                        ) : null}
                    </button>

                    {editEnabled ? (
                        <button
                            type="button"
                            className={toolsOpen ? 'is-active' : ''}
                            onClick={() => setToolsOpen((current) => !current)}
                        >
                            <EditOutlinedIcon />
                            Инструменты
                        </button>
                    ) : null}

                    {data.access?.allowRegistrationClaim !== false ? (
                        <button
                            type="button"
                            className={claimed ? 'is-saved' : ''}
                            onClick={saveToAccount}
                            disabled={claimed || claiming}
                        >
                            {claimed ? <CheckCircleOutlineIcon /> : <PersonAddAltOutlinedIcon />}
                            {claimed
                                ? 'Сохранено в кабинете'
                                : claiming
                                    ? 'Сохраняем…'
                                    : 'Сохранить проект'}
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className="is-secondary"
                        onClick={() => navigate(savedProject ? '/mySceneProjects' : '/scene-access')}
                    >
                        <ArrowBackOutlinedIcon />
                        Выйти
                    </button>
                </div>
                </header>
            </div>

            <section className="sceneGuestView__canvas">
                {updateAvailable ? (
                    <div className={`sceneGuestView__updateBanner ${guestSceneDirty ? 'has-local-changes' : ''}`}>
                        <SystemUpdateAltOutlinedIcon />
                        <div>
                            <strong>{updateAvailable.message || 'Менеджер обновил сцену'}</strong>
                            <span>
                                {guestSceneDirty
                                    ? 'Доступна новая версия. У вас есть локальные изменения — при обновлении они будут заменены.'
                                    : (updateAvailable.description || 'Доступна новая версия проекта.')}
                            </span>
                        </div>
                        <div className="sceneGuestView__updateActions">
                            <button type="button" onClick={refreshGuestScene} disabled={refreshingScene}>
                                {refreshingScene ? 'Обновляем…' : 'Обновить сцену'}
                            </button>
                            <button type="button" className="is-later" onClick={() => setUpdateAvailable(null)} disabled={refreshingScene}>
                                Позже
                            </button>
                        </div>
                    </div>
                ) : null}

                <Suspense fallback={<div className="sceneGuestView__loading">Загрузка 3D-сцены…</div>}>
                    <ModelViewer
                        key={`${sourceKey}:${data.scene?._id || data.access?._id || ''}:${data.scene?.revision || 0}`}
                        office={data.office}
                        guestAccess={data.access}
                        guestProducts={data.products}
                        guestToken={guestToken}
                        guestToolsOpen={editEnabled && toolsOpen}
                        guestToolsTab={toolsTab}
                        onGuestToolsOpenChange={setToolsOpen}
                        onGuestToolsTabChange={setToolsTab}
                        guestToolsNode={editEnabled ? (
                            <SceneGuestToolsDock
                                open
                                embedded
                                activeTab={toolsTab}
                                onTabChange={setToolsTab}
                                onClose={() => setToolsOpen(false)}
                                permissions={permissions}
                                limits={limits}
                                products={data.products}
                            />
                        ) : null}
                    />
                </Suspense>

                {modelProgress.loading || modelProgress.failed > 0 ? (
                    <div className="sceneGuestView__modelProgress">
                        <strong>
                            {modelProgress.loading
                                ? modelProgress.phase === 'initial'
                                    ? 'Загружаем главные модели сцены'
                                    : 'Догружаем сцену в фоне'
                                : 'Загрузка сцены завершена'}
                        </strong>
                        <span>
                            {modelProgress.loaded}/{modelProgress.total}
                            {modelProgress.failed
                                ? ` · ошибок: ${modelProgress.failed}`
                                : ''}
                        </span>
                    </div>
                ) : null}

                {!permissions.walkMode ? (
                    <div className="sceneGuestView__restriction">
                        Прогулка отключена владельцем проекта
                    </div>
                ) : null}

                {workspaceOpen ? (
                    <aside className="sceneGuestView__workspace">
                        <div className="sceneGuestView__workspaceHeader">
                            <div className="sceneGuestView__workspaceTabs">
                                <button
                                    type="button"
                                    className={workspaceTab === 'chat' ? 'is-active' : ''}
                                    onClick={() => setWorkspaceTab('chat')}
                                >
                                    <ChatOutlinedIcon /> Чат
                                </button>
                            </div>
                            <button
                                type="button"
                                className="sceneGuestView__workspaceClose"
                                onClick={() => setWorkspaceOpen(false)}
                                aria-label="Закрыть панель"
                            >
                                <CloseOutlinedIcon />
                            </button>
                        </div>

                        <div className="sceneGuestView__workspaceBody">
                            {visitor && guestChat ? (
                                <SceneGuestChatPanel
                                    guestToken={guestToken}
                                    chat={guestChat}
                                    visitor={visitor}
                                    pageSessionId={pageSessionIdRef.current}
                                    onVisitorChange={handleVisitorChange}
                                />
                            ) : (
                                <div className="sceneGuestView__workspaceLoading">
                                    Подключаем защищённый чат…
                                </div>
                            )}
                        </div>
                    </aside>
                ) : null}
            </section>

            <SceneGuestCatalogPanel
                open={catalogOpen}
                onClose={() => setCatalogOpen(false)}
                guestToken={guestToken}
                enabled={catalogEnabled}
                pageSize={Math.min(8, Number(data.catalog?.pageSize || 8))}
                onPreviewProduct={handlePreviewProduct}
                previewingProductId={previewingProductId}
            />

            <SceneGuestViewControls
                open={viewControlsOpen}
                onClose={() => setViewControlsOpen(false)}
                authorSettings={data.scene?.snapshot || {}}
            />

            {error ? (
                <div className="sceneGuestView__toast">
                    {error}
                </div>
            ) : null}

            <CreateUserModal compact />
        </main>
    )
}

export default SceneGuestViewerPage
